import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject } from 'rxjs';
import { env } from '../../../environments/environment';
import { DialogService } from '../../dialog/dialog.service';
import { FlagService } from '../../flag/flag.service';
import { SettingService } from '../../setting/setting.service';
import { Flag } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';

@Component({
  selector: 'app-edit-flag-details',
  templateUrl: './edit.flag.details.component.html',
  styleUrls: ['./edit.flag.details.component.scss'],
})
export class EditFlagDetailsComponent implements OnInit, OnChanges, OnDestroy {
  dateConfig = { dateInputFormat: 'MM-DD-YYYY', customTodayClass: 'date-today' };

  @ViewChild('helpPicturesModal', { static: true }) public helpPicturesModal: ModalDirective;

  @Output() flagChanged = new EventEmitter<any>();
  @Output() flagDeleted = new EventEmitter<number>();
  @Output() requestEdit = new EventEmitter<any>();
  @Input() flag: Flag;
  @Input() hideDelete = false;
  @Input() readOnly = false;
  editFlagForm: UntypedFormGroup;
  scoresEnabled = true;
  canUngroup = false;
  fileToUpload: File;

  saving = false;

  hideLegacy = false;

  flagTypes = [];
  get flagSubtypes() {
    const flagType = this.editFlagForm.get('flagType').value;
    if (flagType) {
      return flagType.subtypes;
    } else {
      return [];
    }
  }

  options = ['Option 1', 'Option 2', 'Option 3', 'Option 4'];

  constructor(
    private flagService: FlagService,
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private dialog: DialogService,
    public settingService: SettingService,
  ) {
    this.editFlagForm = this.fb.group({
      flagType: '',
      flagSubtype: '',
      text: '',
      score: 0,
      description: '',
      helpPictures: this.fb.array([]),

      violation: this.fb.group({
        elevatedRisk: '',
        deadline: '',
        whyDoesThisMatter: '',
        actionRequested: '',
        internalNotes: '',
        documentationRequested: '',
        urlTitle: '',
        url: '',
      }),

      discrepancy: this.fb.group({
        stated: '',
        actual: '',
        resultOfTheFinding: '',
        impact: '',
        effectiveDate: '',
        commentsForReport: '',
        urlTitle: '',
        url: '',
      }),
    });
  }

  onChanges = new ReplaySubject<SimpleChanges>();

  async ngOnInit() {
    this.scoresEnabled = await this.settingService.scoresEnabled();

    const subtypeInfo = await this.flagService.subtypeInfo;
    this.flagTypes = await this.flagService.listTypes();
    this.flagTypes.forEach(type => {
      type.subtypes.forEach(subtype => {
        subtype.name = this.flagService.getSubtypeInfo(subtypeInfo, subtype.name).name;
      });
    });

    this.editFlagForm.get('flagType').valueChanges.subscribe(type => {
      if (type) {
        this.editFlagForm.get('flagSubtype').setValue(type ? type.subtypes[0] : null);
      }
    });

    this.editFlagForm.get('flagType').setValue(this.flagTypes[0]);

    this.onChanges.subscribe((changes: SimpleChanges) => {
      if ('readOnly' in changes) {
        if (this.readOnly) {
          this.editFlagForm.disable();
        } else {
          this.editFlagForm.enable();
        }
      }

      if ('flag' in changes) {
        if (this.flagTypes && this.flag && this.flag.subtype && this.flag.subtype.type) {
          this.editFlagForm.get('flagType').setValue(this.flagTypes.find(ft => ft.id === this.flag.subtype.type.id));
          this.editFlagForm.get('flagSubtype').setValue(this.flagSubtypes.find(fst => fst.id === this.flag.subtype.id));
          this.hideLegacy = this.flag.subtype.name !== 'Legacy';
        }

        this.editFlagForm.get('text').setValue(this.flag.text);
        this.editFlagForm.get('description').setValue(this.flag.description);
        this.editFlagForm.get('score').setValue(this.flag.score);
        this.editFlagForm.get('violation').patchValue(this.flag.violation);
        this.editFlagForm.get('discrepancy').patchValue(this.flag.discrepancy);
        this.editFlagForm
          .get('violation.deadline')
          .patchValue(this.flag.violation.deadline ? new Date(this.flag.violation.deadline) : null);

        this.editFlagForm
          .get('discrepancy.effectiveDate')
          .patchValue(this.flag.discrepancy.effectiveDate ? new Date(this.flag.discrepancy.effectiveDate) : null);

        const helpPictures = this.editFlagForm.get('helpPictures') as UntypedFormArray;
        while (helpPictures.length > 0) {
          helpPictures.removeAt(0);
        }

        if (this.flag.helpPictures && this.flag.helpPictures.length) {
          this.flag.helpPictures.forEach(hp => {
            helpPictures.push(
              this.fb.group({
                caption: '',
                title: '',
                ...hp,
              }),
            );
          });
        }

        if (this.flag.groupId) {
          this.editFlagForm.disable();
        }
      }

      this.editFlagForm.get('violation.deadline').disable();
    });

    this.canUngroup = await this.userService.hasPermissions([permissions.FLAG.MANAGER]);
  }

  ngOnChanges(changes) {
    this.onChanges.next(changes);
  }

  ngOnDestroy() {
    this.onChanges.complete();
  }

  clickUnlink() {
    this.dialog.confirm().show({
      message: `Do you really want to unlink the Picture from the Flag?`,
      confirmed: async () => {
        const saved = await this.flagService.edit(this.flag.id, {
          ...this.flag,
          picture: null,
          y: null,
          x: null,
        });
        const newFlag = saved.flags.find(f => f.id === this.flag.id);
        this.flag.picture = null;
        this.flag.y = null;
        this.flag.x = null;

        this.toastr.success('Flag unlinked');
        this.flagChanged.emit(newFlag);
      },
    });
  }

  async save() {
    if (this.saving) return;
    this.saving = true;
    if (this.editFlagForm.valid) {
      const formData = this.editFlagForm.value;
      await this.flagService.edit(this.flag.id, {
        ...this.flag,
        subtype: undefined,
        subtypeId: formData.flagSubtype.id,
        text: formData.text,
        description: formData.description,
        y: this.flag.y,
        x: this.flag.x,
        score: formData.score,
        violation: formData.violation,
        discrepancy: formData.discrepancy,
        helpPictures: formData.helpPictures,
      });
      const newFlag = await this.flagService.get(this.flag.id);
      this.flagChanged.emit(newFlag);
    }
    this.saving = false;
  }

  async remove() {
    this.dialog.confirm().show({
      message: `Do you really want to remove the Flag?`,
      confirmed: async () => {
        await this.flagService.remove(this.flag.id);
        this.flagDeleted.emit(this.flag.id);
      },
    });
  }

  async ungroup() {
    this.dialog.confirm().show({
      message: `Do you really want to ungroup this Flag?`,
      confirmed: async () => {
        await this.flagService.addToGroup(null, this.flag.id);
        this.toastr.success('Flag ungrouped');

        this.flag.groupId = null;
        this.flag.group = null;

        this.editFlagForm.enable();
        this.flagChanged.emit(this.flag);
      },
    });
  }

  setEffectiveDate(to) {
    switch (to) {
      case '+30d':
        const d30 = new Date();
        d30.setDate(d30.getDate() + 30);
        this.editFlagForm.get('discrepancy.effectiveDate').setValue(d30);
        break;

      case '+60d':
        const d60 = new Date();
        d60.setDate(d60.getDate() + 60);
        this.editFlagForm.get('discrepancy.effectiveDate').setValue(d60);
        break;

      case '+90d':
        const d90 = new Date();
        d90.setDate(d90.getDate() + 90);
        this.editFlagForm.get('discrepancy.effectiveDate').setValue(d90);
        break;
    }
  }

  setDeadline(to) {
    switch (to) {
      case '+30d':
        const d30 = new Date();
        d30.setDate(d30.getDate() + 30);
        this.editFlagForm.get('violation.deadline').setValue(d30);
        break;

      case '+60d':
        const d60 = new Date();
        d60.setDate(d60.getDate() + 60);
        this.editFlagForm.get('violation.deadline').setValue(d60);
        break;

      case '+90d':
        const d90 = new Date();
        d90.setDate(d90.getDate() + 90);
        this.editFlagForm.get('violation.deadline').setValue(d90);
        break;
    }
  }

  editFlag() {
    this.requestEdit.emit();
  }

  originalHelpPictures;

  openDocPictures() {
    this.originalHelpPictures = (this.editFlagForm.get('helpPictures') as UntypedFormArray).getRawValue();
    this.helpPicturesModal.show();
  }

  closeHelpPictures() {
    this.helpPicturesModal.hide();
  }

  cancelHelpPictures() {
    this.helpPicturesModal.hide();
    this.editFlagForm.get('helpPictures').setValue(this.originalHelpPictures);
  }

  setUploadFile(files: FileList) {
    this.fileToUpload = files.item(0);
  }

  removeHelpPicture(i) {
    (this.editFlagForm.get('helpPictures') as UntypedFormArray).removeAt(i);
  }

  getSrc(img) {
    return `${env.APIUrl}/image/download/${img.key}`;
  }

  async uploadHelpPicture(input: HTMLInputElement) {
    const formData = new FormData();
    formData.append('upload', this.fileToUpload, this.fileToUpload.name);
    const uploadInfo = await this.flagService.uploadPicture(formData);
    input.value = null;
    (this.editFlagForm.get('helpPictures') as UntypedFormArray).push(
      this.fb.group({
        ...uploadInfo,
        caption: '',
        title: '',
      }),
    );
  }

  /* https://github.com/ng-select/ng-select/issues/809 */
  addTag = x => x;
}
